import { Checkbox } from '@neo/primitives/checkbox'
import { Input } from '@neo/primitives/input'
import { Select } from '@neo/primitives/select'
import { Textarea } from '@neo/primitives/textarea'
import { Typography } from '@neo/primitives/typography'
import { X } from 'lucide-react'
import { useState } from 'react'
import { NodevarList } from '../../components/nodevar-list'
import {
  AddIcon,
  chip,
  DeleteButton,
  DragHandle,
  ExpandableBody,
  FieldBlock,
  RowHead,
  rowShell,
  VariableNameError,
} from './agent-task-row-shell'
import {
  type AgentTaskVariable,
  applyAdvancedChange,
  defaultInitialRef,
  prefillInitialRef,
  TYPE_ADVANCED,
  TYPE_NOTES,
  TYPE_OPTIONS,
  typeLabel,
} from './agent-tasks-config'

const ChoiceOptionsEditor = ({
  options,
  onChange,
}: {
  options: string[]
  onChange: (next: string[]) => void
}) => {
  const [draft, setDraft] = useState('')
  const commit = () => {
    const value = draft.trim()
    if (!value || options.includes(value)) return
    onChange([...options, value])
    setDraft('')
  }
  return (
    <FieldBlock label="Options">
      <div className="flex flex-wrap items-center gap-1.5 min-h-[40px] p-2 border border-st2 rounded-md bg-background">
        {options.map((option) => (
          <span key={option} className={`inline-flex items-center gap-1 ${chip}`}>
            {option}
            <button
              type="button"
              data-testid="button-remove-option"
              aria-label={`Remove option ${option}`}
              onClick={() => onChange(options.filter((item) => item !== option))}
              className="text-secondary hover:text-primary"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          data-testid="input-add-option"
          aria-label="Add a choice option"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit()
            }
          }}
          onBlur={commit}
          placeholder="Type an option and press Enter"
          className="flex-1 min-w-[140px] bg-transparent text-p-400 outline-none"
        />
      </div>
    </FieldBlock>
  )
}

const ADDRESS_REQUIRED_FIELDS_OPTIONS = [
  { label: 'Full address', value: 'full' },
  { label: 'Country only', value: 'country' },
  { label: 'State only', value: 'state' },
  { label: 'City only', value: 'city' },
  { label: 'Location', value: 'location' },
]

// address collects the full address by default; this narrows it to one preset.
const AddressFieldsEditor = ({
  variableName,
  value,
  onChange,
}: {
  variableName: string
  value: string
  onChange: (next: string) => void
}) => {
  const fieldId = `${variableName}-address-required-fields`
  return (
    <FieldBlock label="Fields to collect" htmlFor={fieldId}>
      <div data-testid="select-address-required-fields">
        <Select
          name={fieldId}
          value={value || 'full'}
          onChange={(next: string | string[]) => onChange(next as string)}
          options={ADDRESS_REQUIRED_FIELDS_OPTIONS}
          searchable={false}
        />
      </div>
    </FieldBlock>
  )
}

// dtmf needs an explicit digit count (the backend constructor requires num_digits > 0),
// plus an opt-in read-back. Mirrors the choice type's special-case editor.
const DtmfFieldsEditor = ({
  variableName,
  numDigits,
  askForConfirmation,
  onNumDigitsChange,
  onConfirmationChange,
}: {
  variableName: string
  numDigits: number | undefined
  askForConfirmation: boolean
  onNumDigitsChange: (next: number | undefined) => void
  onConfirmationChange: (next: boolean) => void
}) => {
  const numDigitsId = `${variableName}-dtmf-num-digits`
  const confirmId = `${variableName}-dtmf-confirm`
  return (
    <FieldBlock label="Number of digits" htmlFor={numDigitsId}>
      <Input
        id={numDigitsId}
        name={numDigitsId}
        data-testid="input-dtmf-num-digits"
        type="number"
        min={1}
        step={1}
        value={numDigits === undefined ? '' : String(numDigits)}
        onChange={(e) => {
          if (e.target.value === '') {
            onNumDigitsChange(undefined)
            return
          }
          const parsed = Number(e.target.value)
          onNumDigitsChange(Number.isNaN(parsed) ? undefined : parsed)
        }}
        placeholder="e.g. 4"
      />
      <div className="mt-2">
        <Checkbox
          id={confirmId}
          name={confirmId}
          data-testid="checkbox-dtmf-confirm"
          label="Confirm the digits before recording"
          value={askForConfirmation}
          onChange={onConfirmationChange}
        />
      </div>
    </FieldBlock>
  )
}

// The initial-value affordance: absent by default. Once attached it carries a
// node-var reference (or a fixed value); silent use is the default and
// "Confirm it first" opts into the read-back. One fixed hint slot below the
// checkbox swaps its text so toggling never shifts the layout.
const InitialValueField = ({
  variableName,
  variableType,
  inputName,
  nodeVars,
  value,
  confirm,
  onValueChange,
  onConfirmChange,
  onRemove,
}: {
  variableName: string
  variableType: string
  inputName: string
  nodeVars: string[]
  value: string
  confirm: boolean
  onValueChange: (value: string) => void
  onConfirmChange: (confirm: boolean) => void
  onRemove: () => void
}) => (
  <FieldBlock
    label={
      <span className="flex items-center justify-between">
        Initial value
        <button
          type="button"
          data-testid="button-remove-initial-value"
          className="font-inter text-xs-500 text-secondary hover:text-status-danger"
          onClick={onRemove}
        >
          Remove
        </button>
      </span>
    }
  >
    <Input
      name={inputName}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      placeholder={`${defaultInitialRef(variableName)} or a fixed value`}
      endIcon={
        <NodevarList
          nodevars={nodeVars}
          className="pr-2"
          inputElementSelector={`input[name="${inputName}"]`}
        />
      }
    />
    <div className="mt-2">
      <Checkbox
        name={`${variableName}-confirm-first`}
        label="Confirm it first"
        value={confirm}
        onChange={onConfirmChange}
      />
    </div>
    <Typography variant="XS/400" className="text-secondary mt-1.5">
      {confirm
        ? 'Read back once at the start of the call; asked again only if wrong or missing.'
        : variableType === 'email'
          ? 'An email address is always read back for confirmation before use, even when provided.'
          : 'Used silently when present; the agent asks only if it is missing or empty.'}
    </Typography>
  </FieldBlock>
)

// One "Variables to collect" card: drag-reorderable collapsible row with name,
// instructions, type (+ per-type extras), initial value, and advanced params.
export const AgentTaskVariableRow = ({
  row,
  index,
  open,
  onToggle,
  dragging,
  onDragStart,
  onDrop,
  nodeVars,
  onChange,
  onDelete,
}: {
  row: AgentTaskVariable
  index: number
  open: boolean
  onToggle: () => void
  dragging: boolean
  onDragStart: () => void
  onDrop: () => void
  nodeVars: string[]
  onChange: (patch: Partial<AgentTaskVariable>) => void
  onDelete: () => void
}) => {
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const advanced = TYPE_ADVANCED[row.type] ?? []
  const note = TYPE_NOTES[row.type]
  const params = row.params ?? {}
  const hasInitial = row.known_value !== undefined
  return (
    <div
      // biome-ignore lint/a11y/noStaticElementInteractions: native HTML5 drag-reorder container
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={`${rowShell} ${dragging ? 'opacity-50' : ''} ${open ? 'border-st1' : ''}`}
    >
      <RowHead open={open} onToggle={onToggle}>
        <DragHandle />
        <div className="flex-1 min-w-0 flex items-center">
          <span className="text-s-600 truncate">{row.name}</span>
          {!row.optional && (
            <span className="text-status-danger ml-0.5 text-s-600" title="Required">
              *
            </span>
          )}
        </div>
        <span className={`${chip} text-secondary`}>{typeLabel(row.type)}</span>
        <DeleteButton onClick={onDelete} />
      </RowHead>
      <ExpandableBody open={open}>
        <FieldBlock label="Variable name">
          <Input
            value={row.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="Enter variable name"
          />
          <VariableNameError name={row.name} />
        </FieldBlock>
        <FieldBlock label="Instructions">
          <Textarea
            value={row.instructions}
            onChange={(e) => onChange({ instructions: e.target.value })}
            placeholder="What to collect, in plain words"
            className="bg-background min-h-[56px]"
          />
        </FieldBlock>
        <FieldBlock label="Type">
          <Select
            value={row.type}
            onChange={(value: string | string[]) => onChange({ type: value as string, params: {} })}
            options={TYPE_OPTIONS}
            searchable={false}
          />
          {note && (
            <Typography variant="XS/400" className="text-secondary mt-1.5">
              {note}
            </Typography>
          )}
        </FieldBlock>
        {row.type === 'choice' && (
          <ChoiceOptionsEditor
            options={(params.choices as string[]) ?? []}
            onChange={(next) => onChange({ params: { ...params, choices: next } })}
          />
        )}
        {row.type === 'address' && (
          <AddressFieldsEditor
            variableName={row.name}
            value={(params.required_fields as string) ?? ''}
            onChange={(next) => onChange({ params: { ...params, required_fields: next } })}
          />
        )}
        {row.type === 'dtmf' && (
          <DtmfFieldsEditor
            variableName={row.name}
            numDigits={typeof params.num_digits === 'number' ? params.num_digits : undefined}
            askForConfirmation={!!params.ask_for_confirmation}
            onNumDigitsChange={(next) => onChange({ params: { ...params, num_digits: next } })}
            onConfirmationChange={(next) =>
              onChange({ params: { ...params, ask_for_confirmation: next } })
            }
          />
        )}
        {hasInitial ? (
          <InitialValueField
            variableName={row.name}
            variableType={row.type}
            inputName={`agent-task-initial-value-${index}`}
            nodeVars={nodeVars}
            value={row.known_value ?? ''}
            confirm={!!row.confirm_if_known}
            onValueChange={(value) => onChange({ known_value: value })}
            onConfirmChange={(confirm) => onChange({ confirm_if_known: confirm })}
            onRemove={() => onChange({ known_value: undefined, confirm_if_known: undefined })}
          />
        ) : (
          <button
            type="button"
            className="flex items-center gap-2 font-inter text-xs-500 text-primary hover:text-primary/80 w-fit"
            onClick={() =>
              onChange({
                known_value: prefillInitialRef(row.name, nodeVars),
                confirm_if_known: false,
              })
            }
          >
            <AddIcon /> Initial value
          </button>
        )}
        {advanced.length > 0 && (
          <div className="border-t border-dashed border-st2 pt-2.5">
            <button
              type="button"
              className="font-inter text-xs-500 text-secondary hover:text-foreground"
              onClick={() => setAdvancedOpen(!advancedOpen)}
            >
              {advancedOpen ? '▾' : '▸'} {typeLabel(row.type)} settings
            </button>
            {advancedOpen && (
              <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-2.5">
                {advanced.map(([key, label, defaultValue, requires]) => {
                  const locked = requires !== undefined && !params[requires]
                  return (
                    <Checkbox
                      key={key}
                      name={`${row.name}-${key}`}
                      label={label}
                      disabled={locked}
                      value={locked ? false : ((params[key] as boolean) ?? defaultValue)}
                      onChange={(checked: boolean) =>
                        onChange({ params: applyAdvancedChange(row.type, params, key, checked) })
                      }
                    />
                  )
                })}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end border-t border-st2 pt-3">
          <Checkbox
            name={`${row.name}-optional`}
            label="Optional"
            value={!!row.optional}
            onChange={(checked: boolean) => onChange({ optional: checked })}
          />
        </div>
      </ExpandableBody>
    </div>
  )
}
